import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
// logic
import { useTranslation } from 'react-i18next';
import {
  constants as loadConstants,
  formatters as loadFormatters,
} from 'utils/loads';
// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectors as settingsSelectors } from 'state/settings';
import loadSelectors from 'state/loads/selectors';
import { actions as loadsActions } from 'state/loads';
// styles
import styled from 'styled-components';
// components
import Select from 'components/CustomSelect';
import { DropdownOption } from 'refactored/ui/form';

export const isCustomPallet = (palletType) =>
  palletType === loadConstants.PALLET_TYPES.CUSTOM_PALLET;

export const formatSubLabel = ({ t, sizes, showParens }) =>
  loadFormatters.formatMeasureGroup({
    t,
    width: sizes[loadConstants.FIELDS.ITEM_WIDTH],
    length: sizes[loadConstants.FIELDS.ITEM_LENGTH],
    showParens,
  });

export const getOptions = ({ palletTypes, t }) => {
  const defaultPalletSizes = loadConstants.PALLET_SIZES;
  return palletTypes.map((palletType) => ({
    value: palletType,
    label: `${t(`labels.${palletType}`)}`,
    subLabel: !isCustomPallet(palletType)
      ? formatSubLabel({
          t,
          sizes: defaultPalletSizes[palletType],
          showParens: true,
        })
      : '(' + t(`sublabels.${palletType}`) + ')',
  }));
};

const PalletTypeDropdown = ({ className, shipmentKey, loadKey }) => {
  const { t } = useTranslation('PalletType');
  const dispatch = useDispatch();
  const value = useSelector((state) =>
    loadSelectors.getPalletType(state, shipmentKey, loadKey)
  );
  const palletTypes = useSelector(settingsSelectors.getPalletTypes);
  const sizes = useSelector((state) =>
    loadSelectors.getLoadSize(state, shipmentKey, loadKey)
  );
  const options = useMemo(
    () => getOptions({ palletTypes, sizes, value, t }),
    [palletTypes, sizes, value, t]
  );

  const customPalletLabel = useMemo(() => {
    const showCustomPalletSizeSublabel =
      sizes[loadConstants.FIELDS.ITEM_LENGTH] !== undefined &&
      sizes[loadConstants.FIELDS.ITEM_WIDTH] !== undefined;

    if (sizes && showCustomPalletSizeSublabel)
      return formatSubLabel({ t, sizes, showParens: false });
  }, [t, sizes]);

  const handleChange = ({ value: selectedValue }) => {
    dispatch(
      loadsActions.changePalletType({
        shipmentKey,
        loadKey,
        value: selectedValue,
        field: loadConstants.FIELDS.PALLET_TYPE,
      })
    );
  };

  return (
    <>
      <div className={className} data-testid="pallet-type">
        <Select
          value={options.find((option) => option.value === value)}
          label={t('label')}
          subLabel={customPalletLabel && `(${customPalletLabel})`}
          placeholder={t('label')}
          options={options}
          isSearchable={false}
          onChange={handleChange}
          components={{ Option: DropdownOption }}
          dataE2E="select_pallet_type"
          t={t}
        />
      </div>
    </>
  );
};

PalletTypeDropdown.propTypes = {
  className: PropTypes.string,
  shipmentKey: PropTypes.string.isRequired,
  loadKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default styled(PalletTypeDropdown)`
  .sub-text {
    color: gray;
  }
`;
